/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, useCallback, useRef, useState } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { CloseOutline, LeftOutline } from 'antd-mobile-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { MarkerIcon } from '~/assets/icons/MarkerIcon';
import { Modal } from '~/components/ui/ImagePinchZoom/Modal';
import { useLocationState } from '~/libs/hooks/useLocationState';

interface IImageProps {
  image: any;
  imageStyle: CSSProperties;
}

const ReportDetailImageViewer = ({ image, imageStyle }: IImageProps) => {
  const { t } = useTranslation('report');
  const navigate = useNavigate();
  const [state, setState] = useLocationState({
    open: false,
    detail: false,
  });
  const [showTopBottomBar, setShowTopBottomBar] = useState(true);

  const toggleImageViewer = () => {
    //이미지 뷰어 토글
    setState({
      open: !state.open,
      detail: false,
    });
  };

  const toggleDetailInfoModal = () => {
    //상세정보 보기 및 닫기
    if (state.detail) {
      navigate(-1);
    } else {
      setState({
        open: true,
        detail: true,
      });
    }
  };

  const handleClickDetailInfo = () => {
    // 공정사진 위 도면 및 위치/설명 눌렀을 때 상세보기 모달 토글
    setState({ open: true, detail: true });
  };

  const clickImage = () => {
    setShowTopBottomBar(!showTopBottomBar);
  };

  const { location, memo } = image;

  const {
    metadata: {
      process_group_name,
      process_id,
      process_name,
      drawingFile_name,
      drawingFile_id,
      pdfImageUrl,
    },
  } = image;

  const imgRef = useRef(null);
  const drawingImgRef = useRef(null);

  const onUpdate = useCallback(({ x, y, scale }: any) => {
    const { current: img }: any = imgRef;

    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty('transform', value);
    }
  }, []);

  const zoomInOutDrawingImg = useCallback(({ x, y, scale }: any) => {
    const { current: img }: any = drawingImgRef;

    if (drawingImgRef) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty('transform', value);
    }
  }, []);

  const GlobalStyle = createGlobalStyle`
        body {
            overflow: hidden;
        }
    `;

  return (
    <>
      {state.open ? (
        <>
          <Modal zIndex={1000} onClick={() => navigate(-1)} header={false}>
            <GlobalStyle />

            <Wrapper>
              {showTopBottomBar && (
                <Box
                  width="100%"
                  height="50px"
                  background="rgba(0, 0, 0, 0.4)"
                  position="absolute"
                  top={0}
                  zIndex={99}
                >
                  <Box
                    padding="12px"
                    position="fixed"
                    left={0}
                    zIndex={99}
                    onClick={() => navigate(-1)}
                  >
                    <LeftOutline color="white" fontSize="20px" />
                  </Box>
                </Box>
              )}

              <QuickPinchZoom onUpdate={onUpdate} key={image._id}>
                <div
                  ref={imgRef}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100vw',
                    height: '100dvh',
                  }}
                  onClick={clickImage}
                >
                  <img src={image.url} style={{ width: '100vw', background: 'var(--white)' }} />
                </div>
              </QuickPinchZoom>

              {showTopBottomBar && (
                <Box
                  background="rgba(0, 0, 0, 0.4)"
                  width="100%"
                  height="50px"
                  position="absolute"
                  bottom="0"
                >
                  <DetailInfoViewButton
                    isOpenImageDetail={state.detail}
                    onClick={() => toggleDetailInfoModal()}
                  >
                    {t('report:m_more_information')}{' '}
                    {state.detail ? t('report:m_close') : t('report:m_example')}
                  </DetailInfoViewButton>
                </Box>
              )}
            </Wrapper>
          </Modal>

          {state.detail && (
            <Modal zIndex={1012} onClick={toggleDetailInfoModal} header={false}>
              <GlobalStyle />
              <Box
                width="100%"
                height="5vh"
                padding="12px"
                background="#000"
                position="fixed"
                left={0}
                zIndex={99}
                onClick={toggleDetailInfoModal}
              >
                <CloseOutline color="white" fontSize="24px" />
              </Box>
              <Box position="absolute" top="10%" overflow="auto" height="80vh">
                <Box padding="0 16px">
                  <Box marginBottom="32px">
                    <InfoLabel>{t('report:m_shooting_date')}</InfoLabel>
                    <InfoValue>{moment(image.updatedAt).format('YYYY-MM-DD')}</InfoValue>
                  </Box>

                  {process_id && (
                    <Box marginBottom="32px">
                      <InfoLabel>{t('report:m_process_name')}</InfoLabel>
                      <InfoValue>{`${process_group_name} > ${process_name}`}</InfoValue>
                    </Box>
                  )}

                  {drawingFile_name && (
                    <Box marginBottom="20px">
                      <InfoLabel>{t('report:m_drawing_name')}</InfoLabel>
                      <InfoValue>{drawingFile_name}</InfoValue>
                    </Box>
                  )}
                </Box>

                {/* 도면 이미지 */}
                {drawingFile_id && (
                  <Box display="flex" justifyContent="center" width="100vw" marginBottom="32px">
                    <QuickPinchZoom onUpdate={zoomInOutDrawingImg} key={image?.metadata._id}>
                      <div ref={drawingImgRef}>
                        <div
                          style={{
                            position: 'absolute',
                            left: (360 / image.metadata.width) * image.metadata.x,
                            top: (240 / image.metadata.height) * image.metadata.y,
                            transform: 'translate(-50%, -100%)',
                          }}
                        >
                          <MarkerIcon size="17" style={{ display: 'block' }} />
                        </div>

                        <div>
                          <img
                            src={pdfImageUrl}
                            style={{
                              width: '360px',
                              height: '240px',
                              background: 'var(--white)',
                            }}
                          />
                        </div>
                      </div>
                    </QuickPinchZoom>
                  </Box>
                )}

                <Box padding="0 16px">
                  {location && (
                    <Box marginBottom="32px">
                      <InfoLabel>{t('report:m_location')}</InfoLabel>
                      <InfoValue>{location}</InfoValue>
                    </Box>
                  )}

                  {memo && (
                    <>
                      <InfoLabel>{t('report:m_described')}</InfoLabel>
                      <InfoValue>{memo}</InfoValue>
                    </>
                  )}
                </Box>
              </Box>
              <Box width="100%" height="10vh" background={'black'}>
                <DetailInfoCloseButton onClick={toggleDetailInfoModal}>
                  {t('report:m_close_details')}
                </DetailInfoCloseButton>
              </Box>
            </Modal>
          )}

          <img key={image?._id} src={image.url} style={imageStyle} />
        </>
      ) : (
        <Box marginBottom="8px">
          <Box position="relative">
            <img key={image?._id} src={image.url} style={imageStyle} onClick={toggleImageViewer} />
            {drawingFile_id && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '4px',
                  right: 4,
                }}
              >
                <div
                  style={{
                    width: '150px',
                    height: '100px',
                    position: 'relative',
                  }}
                  onClick={handleClickDetailInfo}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: (150 / image.metadata.width) * image.metadata.x,
                      top: (100 / image.metadata.height) * image.metadata.y,
                      transform: 'translate(-50%, -100%)',
                      zIndex: 3,
                    }}
                  >
                    <MarkerIcon size="12" style={{ display: 'block' }} />
                  </div>

                  <div>
                    <img
                      style={{
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                      }}
                      src={image.metadata.pdfImageUrl}
                      alt={image.metadata.drawingFile_name}
                    />
                  </div>
                </div>
              </div>
            )}
          </Box>

          {location || memo ? (
            <ImgInfo onClick={handleClickDetailInfo}>
              {location && (
                <ImgInfoWrap>
                  <ImgInfoLabel>{t('report:m_location')}</ImgInfoLabel>
                  <SPre>{location}</SPre>
                </ImgInfoWrap>
              )}

              {memo && (
                <ImgInfoWrap>
                  <ImgInfoLabel>{t('report:m_described')}</ImgInfoLabel>
                  <SPre>{memo}</SPre>
                </ImgInfoWrap>
              )}
            </ImgInfo>
          ) : null}
        </Box>
      )}
    </>
  );
};

const ImgInfoWrap = styled.div`
  display: flex;
  margin-bottom: 8px;

  :last-child {
    margin-bottom: 0;
  }
`;

const SPre = styled.pre`
  margin: 0;
  width: fit-content;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #666666;
`;

const ImgInfoLabel = styled.div`
  width: fit-content;
  margin-right: 8px;
  color: #414c5c;
  flex: 1;
  white-space: nowrap;
`;

const ImgInfo = styled.div`
  padding: 8px 16px;
  background: #fff;
`;

const InfoValue = styled.pre`
  width: 100%;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 16px;
  color: #fff;
`;

const InfoLabel = styled.div`
  color: #7bbefc;
`;

const DetailInfoViewButton = styled.div<{ isOpenImageDetail: boolean }>`
  ${({ isOpenImageDetail }) => `
        display: ${isOpenImageDetail ? 'none' : 'block'};
    `}
  min-width: 140px;
  max-width: auto;
  padding: 5px;
  border: 1px solid #fff;
  border-radius: 30px;
  color: #fff;
  z-index: 99;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translate(-45%, -20%);
  text-align: center;
`;

const DetailInfoCloseButton = styled.div`
  min-width: 140px;
  max-width: auto;
  padding: 5px;
  border: 1px solid #fff;
  border-radius: 30px;
  color: #fff;
  z-index: 99;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translate(-45%, -20%);
  text-align: center;
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
`;

export default ReportDetailImageViewer;
